import React, { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  Typography,
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Checkbox,
  FormGroup,
  Select,
  MenuItem,
  Switch,
  CircularProgress,
  Box
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { authenticatedFetch } from '../../services/api/apiClient'
import { apiEndpoints } from '../../consts'
import styles from './ManualQAFormDialog.module.scss'
import shared from './DrawerTabs.module.scss'

const BASELINE_CRITERIA = {
  callerNameConfirmation: 'Caller Name Confirmation',
  opposingPartyPhonetics: 'Opposing Party Phonetics',
  phoneNumberVerification: 'Phone Number Verification',
  emailPhonetics: 'Email Phonetics',
  companyNameConfirmation: 'Company Name Confirmation',
  callNotes5Ws: 'Call Notes 5Ws'
}

export default function ManualQAFormDialog({
  open,
  onClose,
  callId,
  campaignName,
  existingQAEvaluation,
  existingManualQA,
  onSuccess
}) {
  const [loading, setLoading] = useState(false)
  const [formQuestions, setFormQuestions] = useState([])
  const [formResponses, setFormResponses] = useState({})
  const [enableOverride, setEnableOverride] = useState(false)
  const [overrides, setOverrides] = useState({})
  const [error, setError] = useState(null)
  const [validationErrors, setValidationErrors] = useState({})

  const getOriginalOverrides = useCallback(() => {
    if (!existingQAEvaluation?.criteria) return {}

    const original = {}
    Object.entries(existingQAEvaluation.criteria).forEach(([key, criterion]) => {
      original[key] = {
        originalResult: criterion.result,
        newResult: criterion.result,
        reason: ''
      }
    })
    if (existingQAEvaluation.customCriteria) {
      Object.entries(existingQAEvaluation.customCriteria).forEach(([key, criterion]) => {
        original[key] = {
          originalResult: criterion.result,
          newResult: criterion.result,
          reason: '',
          isCustom: true
        }
      })
    }
    return original
  }, [existingQAEvaluation])

  const getSavedOverrides = useCallback(() => {
    if (!existingManualQA?.autoQAOverride || !existingQAEvaluation?.criteria) return null

    const saved = getOriginalOverrides()
    Object.entries(existingManualQA.autoQAOverride).forEach(([key, override]) => {
      if (saved[key]) {
        saved[key] = {
          ...saved[key],
          newResult: override.newResult,
          reason: override.reason || ''
        }
      }
    })
    return saved
  }, [existingManualQA, existingQAEvaluation, getOriginalOverrides])

  const fetchFormConfig = useCallback(async () => {
    if (!campaignName) return

    try {
      const response = await authenticatedFetch(`${apiEndpoints.qaManualFormConfig}?campaign=${encodeURIComponent(campaignName)}`)
      const data = await response.json()
      setFormQuestions(data.data || [])
    } catch (err) {
      console.error('Error fetching form config:', err)
      setFormQuestions([])
    }
  }, [campaignName])

  useEffect(() => {
    if (open) {
      fetchFormConfig()

      setError(null)
      setValidationErrors({})

      setFormResponses(existingManualQA?.responses || {})

      const savedOverrides = getSavedOverrides()
      if (savedOverrides) {
        setEnableOverride(true)
        setOverrides(savedOverrides)
      } else {
        setEnableOverride(false)
        setOverrides(getOriginalOverrides())
      }
    }
  }, [open, existingManualQA, fetchFormConfig, getSavedOverrides, getOriginalOverrides])

  const handleToggleChange = (checked) => {
    setEnableOverride(checked)
    if (!checked) {
      const original = {}
      if (existingQAEvaluation?.criteria) {
        Object.entries(existingQAEvaluation.criteria).forEach(([key, criterion]) => {
          original[key] = {
            originalResult: criterion.result,
            newResult: criterion.result,
            reason: ''
          }
        })
      }
      if (existingQAEvaluation?.customCriteria) {
        Object.entries(existingQAEvaluation.customCriteria).forEach(([key, criterion]) => {
          original[key] = {
            originalResult: criterion.result,
            newResult: criterion.result,
            reason: '',
            isCustom: true
          }
        })
      }
      setOverrides(original)

      setValidationErrors(prev => {
        const newErrors = { ...prev }
        Object.keys(newErrors).forEach(key => {
          if (key.startsWith('override_')) {
            delete newErrors[key]
          }
        })
        return newErrors
      })
    }
  }

  const handleResponseChange = (questionKey, value) => {
    setFormResponses(prev => ({ ...prev, [questionKey]: value }))
    setValidationErrors(prev => {
      const newErrors = { ...prev }
      delete newErrors[questionKey]
      return newErrors
    })
  }

  const handleCheckboxChange = (questionKey, optionValue, checked) => {
    setFormResponses(prev => {
      const currentValues = prev[questionKey] || []
      if (checked) {
        return { ...prev, [questionKey]: [...currentValues, optionValue] }
      } else {
        return { ...prev, [questionKey]: currentValues.filter(v => v !== optionValue) }
      }
    })
  }

  const handleOverrideChange = (criterionKey, field, value) => {
    setOverrides(prev => {
      const current = prev[criterionKey]
      if (!current) return prev

      return {
        ...prev,
        [criterionKey]: {
          ...current,
          [field]: value
        }
      }
    })

    if (field === 'reason') {
      setValidationErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[`override_${criterionKey}`]
        return newErrors
      })
    }
  }

  const validateForm = () => {
    const errors = {}

    formQuestions.forEach(question => {
      if (question.isRequired) {
        const value = formResponses[question.questionKey]
        if (!value || (Array.isArray(value) && value.length === 0)) {
          errors[question.questionKey] = 'This field is required'
        }
      }
    })

    if (enableOverride) {
      Object.entries(overrides).forEach(([key, override]) => {
        if (override.newResult !== override.originalResult && !override.reason?.trim()) {
          errors[`override_${key}`] = 'Reason is required when changing the result'
        }
      })
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      setError('Please fix the validation errors before submitting')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const userEmail = localStorage.getItem('userEmail') || 'admin'

      const changedOverrides = {}
      if (enableOverride) {
        Object.entries(overrides).forEach(([key, override]) => {
          if (override.newResult !== override.originalResult) {
            changedOverrides[key] = {
              newResult: override.newResult,
              reason: override.reason
            }
          }
        })
      }

      const payload = {
        campaignName,
        responses: formResponses,
        autoQAOverride: Object.keys(changedOverrides).length > 0 ? changedOverrides : null,
        submittedBy: userEmail
      }

      const method = existingManualQA ? 'PUT' : 'POST'
      const response = await authenticatedFetch(apiEndpoints.qaManualByCall(callId), {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to save manual QA')
      }

      onSuccess && onSuccess()
      onClose()
    } catch (err) {
      console.error('Error submitting manual QA:', err)
      setError(err.message || 'Failed to submit manual QA')
    } finally {
      setLoading(false)
    }
  }

  const renderFormQuestion = (question) => {
    const value = formResponses[question.questionKey]
    const hasError = validationErrors[question.questionKey]

    switch (question.questionType) {
      case 'text':
        return (
          <Box className={styles.formField}>
            <label className={styles.formLabel}>
              {question.questionText}
              {question.isRequired && <span className={styles.required}>*</span>}
            </label>
            <TextField
              fullWidth
              value={value || ''}
              onChange={(e) => handleResponseChange(question.questionKey, e.target.value)}
              error={!!hasError}
              helperText={hasError}
              variant="outlined"
              size="small"
              className={styles.textInput}
            />
          </Box>
        )

      case 'textarea':
        return (
          <Box className={styles.formField}>
            <label className={styles.formLabel}>
              {question.questionText}
              {question.isRequired && <span className={styles.required}>*</span>}
            </label>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={value || ''}
              onChange={(e) => handleResponseChange(question.questionKey, e.target.value)}
              error={!!hasError}
              helperText={hasError}
              variant="outlined"
              size="small"
              className={styles.textInput}
            />
          </Box>
        )

      case 'radio':
        return (
          <Box className={styles.formField}>
            <FormControl component="fieldset" error={!!hasError} fullWidth>
              <FormLabel component="legend" className={styles.formLabel}>
                {question.questionText}
                {question.isRequired && <span className={styles.required}>*</span>}
              </FormLabel>
              <RadioGroup
                value={value || ''}
                onChange={(e) => handleResponseChange(question.questionKey, e.target.value)}
                className={styles.radioGroup}
              >
                {question.options?.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio color="primary" />}
                    label={option.label}
                    className={styles.radioOption}
                  />
                ))}
              </RadioGroup>
              {hasError && <Typography variant="caption" color="error">{hasError}</Typography>}
            </FormControl>
          </Box>
        )

      case 'checkbox':
        return (
          <Box className={styles.formField}>
            <FormControl component="fieldset" error={!!hasError} fullWidth>
              <FormLabel component="legend" className={styles.formLabel}>
                {question.questionText}
                {question.isRequired && <span className={styles.required}>*</span>}
              </FormLabel>
              <FormGroup className={styles.checkboxGroup}>
                {question.options?.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    control={
                      <Checkbox
                        checked={(value || []).includes(option.value)}
                        onChange={(e) => handleCheckboxChange(question.questionKey, option.value, e.target.checked)}
                        color="primary"
                      />
                    }
                    label={option.label}
                    className={styles.checkboxOption}
                  />
                ))}
              </FormGroup>
              {hasError && <Typography variant="caption" color="error">{hasError}</Typography>}
            </FormControl>
          </Box>
        )

      case 'select':
        return (
          <Box className={styles.formField}>
            <label className={styles.formLabel}>
              {question.questionText}
              {question.isRequired && <span className={styles.required}>*</span>}
            </label>
            <FormControl fullWidth error={!!hasError} variant="outlined" size="small">
              <Select
                value={value || ''}
                onChange={(e) => handleResponseChange(question.questionKey, e.target.value)}
                className={styles.selectInput}
                SelectDisplayProps={{ 'data-testid': `select-${question.questionKey}` }}
              >
                <MenuItem value="">
                  <em>Select an option</em>
                </MenuItem>
                {question.options?.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {hasError && <Typography variant="caption" color="error">{hasError}</Typography>}
            </FormControl>
          </Box>
        )

      default:
        return null
    }
  }

  const renderCriteriaItem = (key, criterion, isCustom = false) => {
    const override = overrides[key]
    if (!override) return null

    const hasChanged = override.newResult !== override.originalResult
    const hasError = validationErrors[`override_${key}`]

    return (
      <Box key={key} className={`${styles.criteriaItem} ${hasChanged ? styles.criteriaItemChanged : ''}`}>
        <Box className={styles.criteriaHeader}>
          <Typography className={styles.criteriaName}>
            {isCustom ? (criterion.criterionName || key) : (BASELINE_CRITERIA[key] || key)}
            {isCustom && <span className={styles.customBadge}>Custom</span>}
          </Typography>
        </Box>
        <Box className={`${styles.criteriaBadge} ${styles[`badge${criterion.result}`]}`}>
          {criterion.result === 'N_A' ? 'N/A' : criterion.result}
        </Box>
        {hasChanged && (
          <FontAwesomeIcon icon="arrow-right" className={styles.changeArrow} />
        )}
        <Box className={styles.criteriaActions}>
          <Box className={styles.segmentedControl}>
            <button
              type="button"
              className={`${styles.segmentBtn} ${styles.segmentPass} ${override.newResult === 'PASS' ? styles.segmentActive : ''}`}
              onClick={() => handleOverrideChange(key, 'newResult', 'PASS')}
            >
              <FontAwesomeIcon icon="check" />
              Pass
            </button>
            <button
              type="button"
              className={`${styles.segmentBtn} ${styles.segmentFail} ${override.newResult === 'FAIL' ? styles.segmentActive : ''}`}
              onClick={() => handleOverrideChange(key, 'newResult', 'FAIL')}
            >
              <FontAwesomeIcon icon="times" />
              Fail
            </button>
            <button
              type="button"
              className={`${styles.segmentBtn} ${styles.segmentNA} ${override.newResult === 'N_A' ? styles.segmentActive : ''}`}
              onClick={() => handleOverrideChange(key, 'newResult', 'N_A')}
            >
              <FontAwesomeIcon icon="minus" />
              N/A
            </button>
          </Box>
        </Box>
        {hasChanged && (
          <Box className={styles.criteriaReason}>
            <Typography className={styles.reasonLabel}>
              <FontAwesomeIcon icon="pen" />
              Reason for change
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Why are you overriding this result?"
              value={override.reason || ''}
              onChange={(e) => handleOverrideChange(key, 'reason', e.target.value)}
              error={!!hasError}
              helperText={hasError}
              variant="outlined"
            />
          </Box>
        )}
      </Box>
    )
  }

  if (!open) return null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ className: styles.dialogPaper }}
    >
      {/* Header */}
      <Box className={styles.dialogHeader}>
        <Box className={styles.dialogHeaderContent}>
          <Box className={styles.dialogIcon}>
            <FontAwesomeIcon icon="clipboard-check" />
          </Box>
          <Box>
            <Typography className={styles.dialogTitle}>
              {existingManualQA ? 'Edit Manual QA' : 'Manual QA Review'}
            </Typography>
            <Typography className={styles.dialogSubtitle}>
              Call #{callId} {campaignName && `• ${campaignName}`}
            </Typography>
          </Box>
        </Box>
        <button className={styles.closeButton} onClick={onClose} type="button">
          <FontAwesomeIcon icon="times" />
        </button>
      </Box>

      {/* Content */}
      <Box className={styles.dialogContent}>
        {/* Error Alert */}
        {error && (
          <Box className={styles.errorAlert}>
            <FontAwesomeIcon icon="exclamation-circle" />
            <span>{error}</span>
            <button onClick={() => setError(null)} type="button">
              <FontAwesomeIcon icon="times" />
            </button>
          </Box>
        )}

        {/* Existing QA Meta */}
        {existingManualQA && (
          <Box className={styles.metaInfo}>
            <FontAwesomeIcon icon="history" />
            <span>
              Last updated by <strong>{existingManualQA.submittedBy}</strong> on{' '}
              {new Date(existingManualQA.submittedAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
              })}
            </span>
          </Box>
        )}

        {/* Form Questions Section */}
        {formQuestions.length > 0 && (
          <Box className={styles.section}>
            <Box className={styles.sectionHeader}>
              <FontAwesomeIcon icon="list-alt" />
              <Typography className={styles.sectionTitle}>Campaign Questions</Typography>
            </Box>
            <Box className={styles.sectionContent}>
              {formQuestions.map((question) => (
                <Box key={question.questionKey}>
                  {renderFormQuestion(question)}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {formQuestions.length === 0 && (
          <Box className={styles.infoBox}>
            <FontAwesomeIcon icon="info-circle" />
            <span>No campaign-specific questions configured. You can override Auto QA results below.</span>
          </Box>
        )}

        {/* Auto QA Override Section */}
        {existingQAEvaluation && (
          <Box className={styles.section}>
            <Box className={styles.sectionHeader}>
              <Box className={styles.overrideToggle}>
                <Box className={styles.toggleWrapper}>
                  <Switch
                    checked={enableOverride}
                    onChange={(e) => handleToggleChange(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                </Box>
                <Box className={shared.flexGrow}>
                  <Typography className={styles.sectionTitle}>
                    <FontAwesomeIcon icon="sliders-h" />
                    Override AI Scores
                  </Typography>
                  <Typography className={styles.sectionSubtitle}>
                    {enableOverride ? 'Toggle off to reset' : 'Toggle to manually adjust'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {enableOverride && (
              <Box className={styles.overrideContent}>
                {/* Baseline Criteria */}
                <Typography className={styles.criteriaGroupTitle}>Baseline</Typography>
                <Box className={styles.criteriaList}>
                  {Object.entries(existingQAEvaluation.criteria || {}).map(([key, criterion]) =>
                    renderCriteriaItem(key, criterion, false)
                  )}
                </Box>

                {/* Custom Criteria */}
                {existingQAEvaluation.customCriteria && Object.keys(existingQAEvaluation.customCriteria).length > 0 && (
                  <>
                    <Typography className={`${styles.criteriaGroupTitle} ${shared.mt14}`}>
                      Custom
                    </Typography>
                    <Box className={styles.criteriaList}>
                      {Object.entries(existingQAEvaluation.customCriteria).map(([key, criterion]) =>
                        renderCriteriaItem(key, criterion, true)
                      )}
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box className={styles.dialogFooter}>
        <button className={styles.cancelButton} onClick={onClose} disabled={loading} type="button">
          Cancel
        </button>
        <button className={styles.submitButton} onClick={handleSubmit} disabled={loading} type="button">
          {loading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <>
              <FontAwesomeIcon icon="check" />
              {existingManualQA ? 'Update' : 'Submit'}
            </>
          )}
        </button>
      </Box>
    </Dialog>
  )
}
